import {
  ISearchEndpointResponse,
  kSearchDefaultChorePageSize,
  kSearchDefaultLimit,
  SearchArgs,
  searchSchema,
} from "@/src/definitions/search.ts";
import { useEffect } from "react";
import useSWRInfinite from "swr/infinite";
import { z } from "zod";
import { kApiSearchSWRKeys } from "./keys.ts";
import { handleResponse } from "./utils.ts";

async function search([url, params]: [
  url: string,
  params: z.infer<typeof searchSchema>,
]) {
  const res = await fetch(url, {
    method: "POST",
    body: JSON.stringify(params),
  });

  return await handleResponse<ISearchEndpointResponse>(res);
}

export function useSearch(
  opts: SearchArgs & {
    enabled?: boolean;
  }
) {
  const {
    enabled = true,
    query,
    limit = kSearchDefaultLimit,
    chorePageSize = kSearchDefaultChorePageSize,
  } = opts;

  const { data, error, isLoading, isValidating, size, setSize, mutate } =
    useSWRInfinite(
      (pageIndex, previousPageData: ISearchEndpointResponse | null) => {
        if (!enabled) {
          return null;
        }

        if (previousPageData && !previousPageData.choresHasMore) {
          return null;
        }

        return [
          kApiSearchSWRKeys.search,
          {
            query,
            limit,
            chorePage: pageIndex + 1,
            chorePageSize,
          } satisfies SearchArgs,
        ];
      },
      search,
      {
        keepPreviousData: query.trim().length > 0,
        revalidateFirstPage: false,
      }
    );

  useEffect(() => {
    void setSize(1);
  }, [query, enabled, setSize]);

  const firstPage = data?.[0];
  const lastLoadedPage = data?.[data.length - 1];
  const chores = data?.flatMap((page) => page.chores) ?? [];
  const isLoadingMore = Boolean(
    data && size > data.length && typeof data[size - 1] === "undefined"
  );

  return {
    workspaces: firstPage?.workspaces ?? [],
    lists: firstPage?.lists ?? [],
    chores,
    choresHasMore: lastLoadedPage?.choresHasMore ?? false,
    error,
    isLoading,
    isLoadingMore,
    isValidating,
    loadMoreChores: () => {
      if (!enabled || isValidating || isLoadingMore) {
        return;
      }

      if (lastLoadedPage && !lastLoadedPage.choresHasMore) {
        return;
      }

      void setSize(size + 1);
    },
    mutate,
  };
}
